import React, { useCallback, useState } from 'react'
import { Element } from 'react-scroll';
import SectionTitle from '../sectionTitle/SectionTitle';
import Dropzone2 from '../../common/Dropzone2';
import ReactMarkdown from 'react-markdown';
import CopyToClipBoard from '../../common/CopyToClipboard';

function useTranscriptParser() {
  /**
   * Takes input JSON, extracts transcript, and returns a JSON string
   * @param {Object} inputData - The full response object
   * @returns {string} JSON string version of parsed transcript
   */
  const parseTranscript = (inputData) => {
    if (!inputData) {
      return JSON.stringify({ error: "Invalid input" });
    }

    // Extract the transcript array
    const parsedTranscript = inputData.map((entry, index) => ({
      speaker: entry.speaker,
      text: entry.text,
    }));

    const interviewTranscriptString = JSON.stringify({
      parsedTranscript,
    });
    // Return as stringified JSON
    return JSON.stringify(interviewTranscriptString);
  };

  return { parseTranscript };
}

const HRAgentInterview = () => {
  const [file, setFile] = useState([]);
  const [jobDescription, setJobDescription] = useState();
  const [loading, setLoading] = useState(false);
  const [phoneNo, setPhoneNo] = useState('');
  const [isValid, setIsValid] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [sessionID, setSessionID] = useState();
  const [waitMessage, setWaitMessage] = useState("");
  const [interviewTranscripts, setInterviewTranscripts] = useState();
  const [feedback, setFeedback] = useState();
  const [formattedText, setFormattedText] = useState();
  const [parsedResume, setParsedResume] = useState();

  const APIURL = process.env.REACT_APP_TALENTBRIDGE_API;

  const { parseTranscript } = useTranscriptParser();

  const capitalize = (str) => {
    if (!str) {
      return "";
    }
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  const validatePhoneNumber = (input) => {
    // 1. Sanitize the input by removing all non-digit characters
    const sanitizedDigits = input.replace(/[^\d+]/g, '');

    // 2. Check the length of the sanitized string
    if (input.length === 0) {
      setErrorMessage('');
      return;
    }

    // Check if the sanitized number's length is within a common range for international numbers
    if (sanitizedDigits.length >= 10 && sanitizedDigits.length <= 15) {
      setErrorMessage('✅ Valid phone number');
      setIsValid(true);
    } else {
      setErrorMessage(`❌ Invalid number. Expected 10 digits, found ${sanitizedDigits.length}.`);
      setIsValid(false);
    }
  };
  // const [selectedOption, setSelectedOption] = useState('');

  // const interviewTypes = [
  //   {
  //     value: "technical", label: "Technical",
  //   },
  //   {
  //     value: "behavioral", label: "Behavioral",
  //   },
  //   {
  //     value: "situational", label: "Situational",
  //   },
  //   {
  //     value: "general", label: "General",
  //   },
  // ]

  const onDrop = useCallback( (acceptedFiles) => {
    setFile((prevFiles) => [...prevFiles, ...acceptedFiles]);
  }, [setFile]);

	const handleRemove = (fileToRemove) => {
    setFile((prevFiles) =>
      prevFiles.filter((file) => file !== fileToRemove)
    );
  };
  // const handleOptionChange = (event) => {
  //   setSelectedOption(event);
  // };

  const handlePhoneNumberChange = (e) => {
    const input = e.target.value;
    // Allow digits, plus sign, parentheses, and spaces
    const filteredValue = input.replace(/[^0-9+() -]/g, '');
    setPhoneNo(filteredValue);
    validatePhoneNumber(input);
  };

  const formatTranscript = (jsonData) => {
    // Map over each transcript entry to create a formatted string for it
    const transcriptText = jsonData.map(entry => {
      // Add a newline after each entry
      return `${entry.speaker}: ${entry.text.trim()}\n`;
    });

    // Join all the individual entry strings into one complete string
    return transcriptText.join('\n');
  }

  const getParesedResumeText = async () => {
    let parsedResumeText;
    const formdata = new FormData();
    formdata.append("file", file[0]);
    const requestOptions = {
      method: "POST",
      body: formdata,
      redirect: "follow"
    }
    try {
      const response = await fetch(`${APIURL}/parse-document`, requestOptions);
      parsedResumeText = await response.json();
      // console.log(parsedResumeText.text);
    } catch (error) {
      console.error(error);
    }
    return parsedResumeText.text;
  }

  const handleInterview = async (e) => {
    e.preventDefault();
    setLoading(true);
    // setLoading(false);
    
    const resumeText = await getParesedResumeText();
    setParsedResume(resumeText);

    const raw = JSON.stringify({
      "phone_number" : phoneNo,
      "resume_text" : resumeText,
      "job_description_text" : jobDescription
    })
		const requestOptions = {
			method: "POST",
      headers: {
        "Content-Type" : "application/json"
      },
			body: raw,
			redirect: "follow"
		};
    try {
			const response = await fetch(`${APIURL}/interview/voice/start`, requestOptions);
			if(!response.ok) {
				throw new Error(`Voice Interview error! status: ${response.status}`);
			}
			const responseSessionID = await response.json();
      // console.log(responseSessionID);
      setSessionID(responseSessionID.session_id);
    } catch (error) {
      console.error(error);
    }
    setLoading(false);
  }

  const handleViewTranscript = async () => {
    setLoading(true);
    const requestOptions = {
      method: "GET",
      redirect: "follow"
    };
    try {
      const response = await fetch(`${APIURL}/transcript/status/${sessionID}`, requestOptions);
      if(!response.ok) {
        throw new Error(`Transcript error! status: ${response.status}`);
      }
      const voiceTranscripts = await response.json();
      // console.log(voiceTranscripts);
      if(response.status === "pending") {
        setWaitMessage("Processing... Please try again after a few seconds!");
      }
      else {
        setWaitMessage();
        // console.log(voiceTranscripts);
        setInterviewTranscripts(voiceTranscripts.transcript);
        setFormattedText(formatTranscript(voiceTranscripts.transcript));
      }
    } catch (error) {
      console.error(error);
    }
    setLoading(false);
  }

  const handleResetInterview = () => {
    setSessionID();
    setWaitMessage();
    setFile([]);
    setJobDescription();
    setPhoneNo();
    setInterviewTranscripts();
    setFeedback();
  }

  const handleViewFeedback = async () => {
    setLoading(true);
    // console.log(sessionID);
    const parsedTranscriptText = parseTranscript(interviewTranscripts);
    // console.log("parsed", parsedTranscriptText);
    const raw = JSON.stringify({
      "interview_transcript" : parsedTranscriptText,
      "resume" : parsedResume,
      "job_description" : jobDescription
    });
    const requestOptions = {
      method: "POST",
      headers: {
        "Content-Type" : "application/json"
      },
			body: raw,
      redirect: "follow"
    };
    try {
      const response = await fetch(`${APIURL}/feedback`, requestOptions);
      if(!response.ok) {
        throw new Error(`Feedback error! status: ${response.status}`);
      }
      const feedbackResponse = await response.text();
      // console.log(feedbackResponse);
      const lines = feedbackResponse.split('\n');
			let extractedContent = null;
			for (const line of lines) {
				// Check if the line is not empty and starts with the expected prefix
				if (line.trim().startsWith('data: ')) {
					// Remove the "data: " prefix
					const jsonString = line.trim().slice(5);

					// Check if it's the DONE message
					if (jsonString === '[DONE]') {
						// This is the end-of-stream signal, we can stop processing.
						break;
					}

					try {
						const parsedData = JSON.parse(jsonString);
						if (parsedData.feedback) {
							extractedContent = parsedData.feedback;
							break;
						}
					} catch (parseError) {
						console.error('Failed to parse a JSON line:', parseError);
						console.log('Problematic line:', jsonString);
					}
				}
			}
			
			if (extractedContent) {
        // console.log(extractedContent);
				setFeedback(extractedContent);
			} else {
				console.log("No valid analysis content found in the response.");
			}
    } catch (error) {
      console.error(error);
    }
    setLoading(false);
  }

  return (
    <>
      <Element
        name="voice-interview"
        className="datatalent-section-gap bg-color-white"
      >
        <div className="container eduvibe-animated-shape">
          <div className="row g-5 align-items-start">
            <div className="col-12 mt-0 p-0">
              <SectionTitle
                // slogan = "HR Agent"
                classes = "text-center"
                title = "AI Voice Interview Assistant"
              />
            </div>
            <div className="data-talent-description col-12 mt-4 mx-auto px-0 px-md-4">
              <p className="text-center">
                Practice with our AI-powered voice interview system. Get personalized questions based on job descriptions and receive real-time feedback on your responses.
              </p>
            </div>
            <div className="d-flex flex-column flex-md-row px-0">
              <div className="col-12 col-lg-4 px-0 px-md-2 py-2 py-md-0">
                <div className="border rounded-4 p-4 d-flex flex-column align-items-start">
                  <div className="rounded-4 p-4" style={{"backgroundColor": "var(--color-secondary-alt)"}}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--color-secondary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-lov-id="src\components\InterviewAssistantSection.tsx:30:16" data-lov-name="MessageSquare" data-component-path="src\components\InterviewAssistantSection.tsx" data-component-line="30" data-component-file="InterviewAssistantSection.tsx" data-component-name="MessageSquare" data-component-content="%7B%22className%22%3A%22w-6%20h-6%20text-primary%22%7D">
                      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                    </svg>
                  </div>
                  <h4>Tailored Questions</h4>
                  <p>AI generates relevant interview questions based on the specific job description and role requirements.</p>
                </div>
              </div>
              <div className="col-12 col-lg-4 px-0 px-md-2 py-2 py-md-0">
                <div className="border rounded-4 p-4 d-flex flex-column align-items-start">
                  <div className="rounded-4 p-4" style={{"backgroundColor": "var(--color-secondary-alt)"}}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--color-tertiary-2)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-lov-id="src\components\InterviewAssistantSection.tsx:44:16" data-lov-name="Mic" data-component-path="src\components\InterviewAssistantSection.tsx" data-component-line="44" data-component-file="InterviewAssistantSection.tsx" data-component-name="Mic" data-component-content="%7B%22className%22%3A%22w-6%20h-6%20text-career-growth%22%7D">
                      <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z"></path><path d="M19 10v2a7 7 0 0 1-14 0v-2"></path><line x1="12" x2="12" y1="19" y2="22"></line>
                    </svg>
                  </div>
                  <h4>Voice Interaction</h4>
                  <p>Practice speaking naturally with our voice-enabled AI that listens and responds like a real interviewer.</p>
                </div>
              </div>
              <div className="col-12 col-lg-4 px-0 px-md-2 py-2 py-md-0">
                <div className="border rounded-4 p-4 d-flex flex-column align-items-start">
                  <div className="rounded-4 p-4" style={{"backgroundColor": "var(--color-secondary-alt)"}}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--color-success)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-lov-id="src\components\InterviewAssistantSection.tsx:58:16" data-lov-name="Star" data-component-path="src\components\InterviewAssistantSection.tsx" data-component-line="58" data-component-file="InterviewAssistantSection.tsx" data-component-name="Star" data-component-content="%7B%22className%22%3A%22w-6%20h-6%20text-career-success%22%7D">
                      <path d="M11.525 2.295a.53.53 0 0 1 .95 0l2.31 4.679a2.123 2.123 0 0 0 1.595 1.16l5.166.756a.53.53 0 0 1 .294.904l-3.736 3.638a2.123 2.123 0 0 0-.611 1.878l.882 5.14a.53.53 0 0 1-.771.56l-4.618-2.428a2.122 2.122 0 0 0-1.973 0L6.396 21.01a.53.53 0 0 1-.77-.56l.881-5.139a2.122 2.122 0 0 0-.611-1.879L2.16 9.795a.53.53 0 0 1 .294-.906l5.165-.755a2.122 2.122 0 0 0 1.597-1.16z"></path>
                    </svg>
                  </div>
                  <h4>Instant Feedback</h4>
                  <p>Get detailed feedback on your responses, including communication skills, content quality, and improvement tips.</p>
                </div>
              </div>
            </div>
            <div className="col-12 col-lg-6 px-0 px-md-4 py-0">
              <div className="border rounded-4 p-4">
                <div className="h5">Interview Session Features</div>
                <div className="d-flex flex-column flex-md-row">
                  <div className="col-12 col-lg-6 px-0 px-md-2">
                    <div className="border rounded-5 p-4">
                      <div>
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--color-secondary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-lov-id="src\components\InterviewAssistantSection.tsx:82:20" data-lov-name="Clock" data-component-path="src\components\InterviewAssistantSection.tsx" data-component-line="82" data-component-file="InterviewAssistantSection.tsx" data-component-name="Clock" data-component-content="%7B%22className%22%3A%22w-6%20h-6%20text-primary%20mb-2%22%7D">
                          <circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline>
                        </svg>
                      </div>
                      <p className="mb-0"><strong>Flexible Duration</strong></p>
                      <p className="mb-0">upto 5 minute sessions</p>
                    </div>
                  </div>
                  <div className="col-12 col-lg-6 px-0 px-md-2 mt-4 mt-md-0">
                    <div className="border rounded-5 p-4">
                      <div>
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--color-tertiary-2)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-lov-id="src\components\InterviewAssistantSection.tsx:87:20" data-lov-name="MessageSquare" data-component-path="src\components\InterviewAssistantSection.tsx" data-component-line="87" data-component-file="InterviewAssistantSection.tsx" data-component-name="MessageSquare" data-component-content="%7B%22className%22%3A%22w-6%20h-6%20text-career-growth%20mb-2%22%7D">
                          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                        </svg>
                      </div>
                      <p className="mb-0"><strong>Multiple Rounds</strong></p>
                      <p className="mb-0">Technical, behavioral, situational</p>
                    </div>
                  </div>
                </div>
                <p className="mt-4 mb-2"><strong>Session Types Available:</strong></p>
                <div className="d-flex flex-row flex-wrap gap-3">
                  <div className="tb-tag">Technical Interview</div>
                  <div className="tb-tag">Behavioral Questions</div>
                  <div className="tb-tag">Situational Scenarios</div>
                  <div className="tb-tag">Leadership Assessment</div>
                  <div className="tb-tag">Culture Fit</div>
                  <div className="tb-tag">Salary Negotiation</div>
                </div>
              </div>
              {/* <div className="border rounded-4 p-4 mt-5">
                <h6>Success Metrics</h6>
                <div className="d-flex flex-column flex-md-row">
                  <div className="col-12 col-lg-6">
                    <p className="mb-0 tb-metrics">92%</p>
                    <p>Interview Success Rate</p>
                  </div>
                  <div className="col-12 col-lg-6">
                    <p className="mb-0 tb-metrics">85%</p>
                    <p>Confidence Improvement</p>
                  </div>
                </div>
              </div> */}
            </div>
            <div className="col-12 col-lg-6 px-0 px-md-4 py-0">
              <div className="border rounded-4 p-4">
                {!sessionID ? <>
                  <div className="resume-upload-container">
                    <h5 className="mb-0">Start Interview Practice</h5>
                    <p className="">Upload your resume to get personalized advice and interview practice.</p>
                    <form onSubmit={handleInterview}>
                      <div>
                        <label htmlFor="resumeFile">Upload Resume (.pdf, .doc, .docx)</label><br></br>
                        <Dropzone2 onDrop={onDrop} onRemove={handleRemove} files={file} accept={{"application/msword": [], "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [], "application/pdf": []}} required />
                      </div>
                      <div className="">
                        <label htmlFor="jobDescription">Job Description</label><br></br>
                        <textarea rows={5} name="jobDesription" placeholder="Paste the job description here" value={jobDescription} onChange={(e) => setJobDescription(e.target.value)} required />
                      </div>
                      <div className="mt--40">
                        <label htmlFor="phonenumber">Phone number (with country code)</label><br></br>
                        <input
                          type="text"
                          name="phonenumber"
                          maxLength={12}
                          placeholder="e.g., +1 (123) 456-7890"
                          value={phoneNo}
                          onChange={handlePhoneNumberChange}
                          // style={{borderColor: errorMessage.startsWith('✅') ? 'green' : errorMessage.length > 0 ? 'red' : '#eeeeee',}}
                          required
                        />
                      </div>
                      {/* {errorMessage && (
                        <p className="mb-0" style={{ color: errorMessage.startsWith('✅') ? 'green' : 'red' }}>
                          {errorMessage}
                        </p>
                      )} */}
                      {/* <div className="">
                        <label>Choose Interview Type</label>
                        <Select className="basic-single"
                          placeholder="Select Interview..."
                          onChange={handleOptionChange}
                          isClearable="true"
                          name="industry"
                          options={interviewTypes}
                        />
                      </div> */}
                      <button className={`edu-btn btn-secondary mt-4${loading ? " disabled" : file.length === 0 ? " disabled" : jobDescription ? isValid ? "" : " disabled" : " disabled"}`} type="submit" disabled={loading ? true : file.length === 0 ? true : jobDescription ? isValid ? false : true : true}>Start Voice Interview</button>
                    </form>
                  </div>
                </> : <>
                  <div className="transcript-container">
                  <h5>Voice Interview</h5>
                    {!interviewTranscripts ? <>
                      {waitMessage ? <p className="mb-4">{waitMessage}</p> : <p>Voice interview starts on your phone. Once the call ends, you can request a transcript using the button below.<br></br><strong>Note</strong>: If you did <strong>not</strong> receive the call, confirm that your phone number includes +1 at the begining.</p>}
                      <button className="edu-btn btn-secondary" onClick={handleViewTranscript}>View Transcripts</button>
                    </> : <>
                      <div className="p-6 d-flex flex-column transcript-wrapper rounded-4">
                        {interviewTranscripts.map((entry, index) => (
                          // Dynamically apply styling based on the speaker
                          <div key={index} className={`d-flex ${entry.speaker === 'user' ? 'justify-content-end' : 'justify-content-start'}`}>
                            <div className={`mt-4 p-4 transcript-bubble ${entry.speaker === 'user' ? 'bg-user text-white' : 'bg-assistant'}`}>
                              <p className="mb-1"><strong>{capitalize(entry.speaker)}:</strong></p>
                              <p className="mb-1">{entry.text}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                      <CopyToClipBoard textToCopy={formattedText} saveFileName="Interview_Transcript" />
                      {!feedback ? <>
                        <button className="edu-btn btn-secondary mt-5" onClick={handleViewFeedback}>Feedback</button>
                      </> : <>
                        <div className="mt-5 resume-analysis-container rounded-4">
                          <ReactMarkdown>{feedback}</ReactMarkdown>
                        </div>
                        <CopyToClipBoard textToCopy={feedback} saveFileName="Interview_Feedback" />
                      </>}
                    </>}
                  </div>
                  <button className="edu-btn btn-primary mt-5" onClick={handleResetInterview}>Try Another</button>
                </>}
                {loading && <div className="loader-overlay mt-4">
								  <img src={`${process.env.PUBLIC_URL}/images/ajax-loader.gif`} alt="loader" />
								</div>}
              </div>
            </div>
          </div>
        </div>
      </Element>
    </>
  )
}

export default HRAgentInterview;